export type ShortcutModifier = 'mod' | 'ctrl' | 'meta' | 'alt' | 'shift';

/**
 * A modifier token, or any literal key label ('J', 'Enter', '/').
 * `mod` resolves to Cmd on Apple platforms and Ctrl everywhere else.
 */
export type ShortcutKey = ShortcutModifier | (string & Record<never, never>);

let cachedIsMac: boolean | undefined;

/**
 * Apple platforms take Cmd; Windows, Linux and ChromeOS take Ctrl. iPadOS
 * reports itself as a Mac, which is what we want for a hardware keyboard.
 * Resolved once — the platform cannot change under a running document.
 */
export const isMacPlatform = () => {
  if (cachedIsMac !== undefined) return cachedIsMac;
  if (typeof navigator === 'undefined') return false;
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } })
      .userAgentData?.platform ||
    navigator.platform ||
    navigator.userAgent;
  cachedIsMac = /mac|iphone|ipad|ipod/i.test(platform);
  return cachedIsMac;
};

const MAC_SYMBOLS: Record<string, string> = {
  mod: '⌘',
  meta: '⌘',
  ctrl: '⌃',
  alt: '⌥',
  shift: '⇧',
  enter: '↵',
  backspace: '⌫',
  escape: 'Esc',
  esc: 'Esc',
};

const OTHER_SYMBOLS: Record<string, string> = {
  mod: 'Ctrl',
  meta: 'Meta',
  ctrl: 'Ctrl',
  alt: 'Alt',
  shift: 'Shift',
  enter: 'Enter',
  backspace: 'Backspace',
  escape: 'Esc',
  esc: 'Esc',
};

const symbolFor = (key: ShortcutKey, isMac: boolean) => {
  const symbols = isMac ? MAC_SYMBOLS : OTHER_SYMBOLS;
  return (
    symbols[key.toLowerCase()] ?? (key.length === 1 ? key.toUpperCase() : key)
  );
};

/**
 * The printable form of a shortcut — '⌘J' on Apple, 'Ctrl+J' elsewhere.
 * Use it for the `aria-label` or `title` of whatever the shortcut triggers;
 * `KeyboardShortcut` renders the same string visually.
 */
export const formatShortcut = (
  keys: ShortcutKey[],
  isMac: boolean = isMacPlatform()
) => keys.map((key) => symbolFor(key, isMac)).join(isMac ? '' : '+');
